import React, {useCallback, useEffect, useState} from 'react';
import Logger from '../../Logger';
import {createFullTheme, createShadowTheme, getShadowTheme} from '../getShadowTheme';
import {formattedThemes} from '../themeDb';
import {CssBaseline} from '@mui/material';
import {ThemeContext} from '../../../Common/Contexts';
import {ThemeProvider as MuiThemeProvider} from '@mui/material/styles';
import utils from "../../utils";
import LoadingView from "../../../Common/LoadingView";
import {observer} from "mobx-react";
import ThemeManager from "./ThemeManager";
import shadowClient from "../../clients/shadow/shadowClient";
import uiStore from "../../stores/uiStore";
import {ShadowTheme} from "../../clients/shadow/interfaces";
import {notifier} from "../../Notifier";

const logger = new Logger("ThemeProvider");

interface ThemeProviderProps {
    appKey: string;
    theme?: string;
    defaultTheme?: string;
    alternateTheme?: string;
    children: React.ReactNode;
    withThemeSelect: boolean;
}

export default observer(function ThemeProvider(
        {
            appKey,
            theme: singleThemeId,
            defaultTheme: defaultThemeId = singleThemeId,
            alternateTheme: alternateThemeId = singleThemeId,
            children,
            withThemeSelect,
        }: ThemeProviderProps
) {

    const prefix = appKey;
    const storageKey = prefix + "-theme";
    const [themes, setThemes] = useState<ShadowTheme[]>([]);
    const [loading, setLoading] = useState(true);
    const [shadowTheme, setTheme] = useState(getShadowTheme(defaultThemeId));

    useEffect(() => {
        shadowClient.getThemes().then(apiThemes => {
            // The picker lists the API's themes (seeded from shadow-api's data/themes.json). Built-in themes it
            // doesn't have yet (somniworks, the default) are added so they can be picked and restored.
            const missing = formattedThemes
                    .filter(local => !apiThemes.some(theme => theme.name === local.name))
                    .map(createShadowTheme);
            setThemes([...missing, ...apiThemes]);
        }).catch(e => {
            notifier.error(`Error while fetching themes. Error: ${e.message}`);
            logger.error("Failed to fetch themes. Error: %o", e);
        }).finally(() => setLoading(false))
    }, []);

    const toggleTheme = useCallback<(theme: ShadowTheme) => void>(utils.throttle((shadowTheme: ShadowTheme) => {
        setTheme(shadowTheme);
        localStorage.setItem(storageKey, shadowTheme.name);
        logger.info(`Toggled theme to ${shadowTheme.name}. Prefix: ${prefix}`);
        return;

    }, 100), []);

    window.toggleTheme = toggleTheme;

    useEffect(() => {
        if (themes.length) {
            const storedThemeId = localStorage.getItem(storageKey);
            const newTheme = themes.filter(theme => theme.name === storedThemeId)[0];

            if (newTheme) {
                setTheme(newTheme)
            }
        }

    }, [themes]);

    const theme = createFullTheme(shadowTheme.data.theme);

    if (loading) {
        return <MuiThemeProvider theme={theme}>
            <LoadingView/>
        </MuiThemeProvider>
    }

    // //@todo crude way of removing scrollbar styling. needs fixing
    // if (uiStore.isMobile) {
    //     // @ts-ignore
    //     const overrides = theme.components?.MuiCssBaseline?.styleOverrides?.["*"];
    //
    //     if (overrides) {
    //         delete overrides["&::-webkit-scrollbar"];
    //         delete overrides["&::-webkit-scrollbar-thumb"];
    //     }
    //
    // }


    return <ThemeContext.Provider value={{shadowTheme: shadowTheme, setTheme: toggleTheme, setThemes, themes}}>
        <MuiThemeProvider theme={theme}>
            <CssBaseline enableColorScheme/>

            {uiStore.themeManagerEnabled ?
                    <ThemeManager withThemeSelect={withThemeSelect}>
                        {children}
                    </ThemeManager> : children}

        </MuiThemeProvider>
    </ThemeContext.Provider>
})

